import { useMemo } from 'react'
import PropTypes from 'prop-types'

import ExclamationOutlined from '@ant-design/icons/ExclamationOutlined'

import AvatarMui from '@mui/material/Avatar'
import Badge from '@mui/material/Badge'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'

import { HideDetailsType, useHideDetails } from '@providers/HideDetailsProvider'
import { CBOCardPopper } from '@components/CBOCard'
import { UserCardPopper } from '@components/UserCard'
import LogoAvatar from '@assets/logos/logo-avatar.svg'

import { getSizeStyle, mapCBO, mapUser } from './Avatar.utils'
import Offline from './Offline'

Avatar.propTypes = {
  hideStatus: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nickname: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    fullName: PropTypes.string,
    outOfOffice: PropTypes.bool,
  }),
  variant: PropTypes.oneOf(['circular', 'rounded', 'square']),
  size: PropTypes.oneOf(['badge', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl']),
  hover: PropTypes.oneOf(['none', 'simple', 'card']),
}

export default function Avatar({ hideStatus = false, user, variant = 'rounded', size = 'md', hover = 'none', sx, ...others }) {
  const { [HideDetailsType.Avatars]: hideAvatar } = useHideDetails()
  const { backgroundColor, initials } = useMemo(() => mapUser(user), [user])

  const content = (
    <Offline in={!hideStatus && user?.outOfOffice} overlap={variant === 'circular' ? 'circular' : 'rectangular'}>
      <AvatarMui
        alt="profile picture"
        src={hideAvatar ? undefined : user?.profilePictureThumbUrl}
        variant={variant}
        sx={[getSizeStyle(size), { color: 'common.white', backgroundColor }, sx]}
        {...others}
      >
        {initials}
      </AvatarMui>
    </Offline>
  )

  // Use userId when it is available because Provider's id is not the same as userId
  const id = user?.userId || user?.id

  if (hover === 'none' || !id || hideAvatar) return content

  if (hover === 'card') return <UserCardPopper userId={id}>{content}</UserCardPopper>

  if (hover === 'simple') return <Tooltip title={user?.fullName}>{content}</Tooltip>
}

Avatar.CBO = function CBO({ data, variant = 'rounded', size = 'md', sx, card = false, ...others }) {
  const { [HideDetailsType.Avatars]: hideAvatar } = useHideDetails()
  const { backgroundColor, initials } = useMemo(() => mapCBO(data), [data])

  const isAcceptingNewPatients = data?.acceptingNewPatients ?? true

  const avatar = (
    <AvatarMui
      alt="cbo logo"
      src={hideAvatar ? undefined : data?.logo}
      variant={variant}
      sx={[getSizeStyle(size), { color: 'primary.contrastText', backgroundColor }, sx]}
      {...others}
    >
      {initials}
    </AvatarMui>
  )

  if (hideAvatar) return avatar
  let content = avatar

  if (!isAcceptingNewPatients) {
    content = (
      <Badge
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          <Stack
            sx={{
              backgroundColor: 'error.main',
              borderRadius: '50%',
              color: 'text.contrast',
              border: '1px solid',
              borderColor: 'error.main',
            }}
          >
            <ExclamationOutlined style={{ fontSize: '12px' }} />
          </Stack>
        }
      >
        {avatar}
      </Badge>
    )
  }

  if (card && data?.id) {
    return <CBOCardPopper cboId={data.id}>{content}</CBOCardPopper>
  }

  return content
}

Avatar.Company = function AvatarCompany({ variant = 'circular', size = 'md', ...others }) {
  return (
    <Avatar
      variant={variant}
      size={size}
      user={{ profilePictureThumbUrl: LogoAvatar, fullName: 'Q P', firstName: 'Q', lastName: 'P' }}
      sx={{ backgroundColor: 'background.default' }}
      {...others}
    />
  )
}
Avatar.Icon = function AvatarIcon({ size, ...others }) {
  return <AvatarMui sx={[getSizeStyle(size), { color: 'secondary.main', backgroundColor: 'background.default' }]} {...others} />
}
